'use client';

import React from 'react';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import R2Image from '@/components/ui/R2Image';
import { LovedayStructuredText } from '@/features/loveday/components/LovedayStructuredText';
import type {
  LovedayCardsWithImageSection,
  LovedayImageCard,
} from '@/features/loveday/hooks/fetchData';
import { scrollToElement } from '@/utils/scrollToElement';

interface CardsWithImageSectionProps {
  data?: LovedayCardsWithImageSection;
}

export const CardsWithImageSection = ({
  data,
}: CardsWithImageSectionProps): React.ReactElement | null => {
  if (!data) return null;

  const items = data.components || [];

  return (
    <section className="py-4 lg:py-5">
      <div className="content-wrapper">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {items.map((item: LovedayImageCard, index: number) => (
            <article
              key={index}
              className="flex flex-col rounded-xl border border-gray-100 p-6 shadow-sm"
            >
              {item.image?.url && (
                <div className="relative mb-4 aspect-4/3 w-full overflow-hidden rounded-lg md:h-134">
                  <R2Image
                    path={item.image.url}
                    alt={item.image.title || ''}
                    className="object-cover"
                  />
                </div>
              )}
              {item.heading && (
                <p className="text-royal-500 text-xl font-bold md:text-2xl">
                  {item.heading}
                </p>
              )}
              {item.descriptionParagraphs ? (
                <LovedayStructuredText
                  paragraphs={item.descriptionParagraphs}
                  className="mt-2 space-y-4"
                  paragraphClassName="text-text-secondary text-base leading-relaxed"
                />
              ) : (
                item.description && (
                  <p className="text-text-secondary mt-2 text-base">
                    {item.description}
                  </p>
                )
              )}
            </article>
          ))}
        </div>
      </div>
      <div className="mt-8 flex w-full justify-center">
        <PrimaryButton
          data={{ title: 'Enquire now', functionality: 'show_form' }}
          onShowForm={() => scrollToElement('form')}
          widthFitContent
        />
      </div>
    </section>
  );
};
